
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CustomerForm } from "./booking/CustomerForm";
import { TimeSlotPicker } from "./booking/TimeSlotPicker";
import { BookingConfirmation } from "./booking/BookingConfirmation";
import { AuthenticationAlert } from "./booking/AuthenticationAlert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Clock } from "lucide-react";
import { useBookingAuth } from "@/hooks/useBookingAuth";
import { useTimeSelection } from "@/hooks/useTimeSelection";
import { useBookingSubmission } from "@/hooks/useBookingSubmission";
import { useProfileCheck } from "@/contexts/ProfileCheckContext";
import { useUserCompleteProfile } from "@/hooks/useUserCompleteProfile";
import { useCalendarBookings } from "@/hooks/calendar/useCalendarBookings";

interface BookingDialogProps {
  serviceName: string;
  serviceId: number;
  selectedDate: Date | undefined;
  selectedTimeSlot: string;
  isOpen: boolean;
  onClose: () => void;
  operatingHours?: any[];
}

export const BookingDialog = ({ 
  serviceName, 
  serviceId, 
  selectedDate,
  selectedTimeSlot,
  isOpen,
  onClose,
  operatingHours = []
}: BookingDialogProps) => {
  const [note, setNote] = useState("");
  const { isAuthenticated } = useBookingAuth();
  const { profile: userProfile } = useUserCompleteProfile();
  const { checkProfileBeforeBooking, redirectToProfile } = useProfileCheck();
  
  // Get booking data for the selected date to show status in time slots
  const { bookings } = useCalendarBookings(serviceId?.toString(), selectedDate || new Date());
  
  const { 
    startTime, 
    endTime, 
    conflictError, 
    setStartTime, 
    setEndTime, 
    resetTimeSelection,
    outsideOperatingHoursError
  } = useTimeSelection(selectedTimeSlot, selectedDate, serviceId, operatingHours);
  
  const { handleBooking, loading } = useBookingSubmission(serviceId, serviceName, onClose);

  const resetForm = () => {
    setNote("");
    resetTimeSelection();
  };

  // Reset loading state and form when dialog opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
      onClose();
    }
  };

  const submitBooking = () => {
    // Check profile completeness before submitting
    if (!checkProfileBeforeBooking()) {
      return;
    }
    
    // Don't allow booking outside operating hours
    if (outsideOperatingHoursError) {
      return;
    }
    
    handleBooking(
      isAuthenticated,
      selectedDate,
      startTime,
      endTime,
      note,
      conflictError,
      resetForm
    );
  };

  // Check if profile has required information (phone from notification preferences)
  const hasCompleteProfile = userProfile && 
    !!userProfile.phone && 
    !!userProfile.address;
  
  const isFormValid = !!selectedDate && !!startTime && !!endTime && 
    !conflictError && !outsideOperatingHoursError;
    
  // Get operating hours for selected day to display to user
  const getTodaysOperatingHours = () => {
    if (!selectedDate || !operatingHours || operatingHours.length === 0) {
      return null;
    }
    
    const dayOfWeek = selectedDate.getDay();
    return operatingHours.find((hours: any) => hours.daysOfWeek.includes(dayOfWeek));
  };
  
  const todaysHours = getTodaysOperatingHours();

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Book {serviceName}</DialogTitle>
          <DialogDescription>
            Fill in your details below to book your appointment.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <AuthenticationAlert isAuthenticated={isAuthenticated} />

          <div className="space-y-4">
            <p className="text-sm font-medium text-gray-900">
              Appointment on {selectedDate && format(selectedDate, "MMMM d, yyyy")}
            </p>
            
            {todaysHours && (
              <div className="flex items-center text-xs text-gray-600 bg-gray-50 p-2 rounded">
                <Clock className="h-3 w-3 mr-1" />
                <span>Operating hours today: {todaysHours.startTime} - {todaysHours.endTime}</span>
              </div>
            )}
            
            <TimeSlotPicker
              startTime={startTime}
              endTime={endTime}
              onStartTimeChange={setStartTime}
              onEndTimeChange={setEndTime}
              isAuthenticated={isAuthenticated}
              selectedTimeSlot={selectedTimeSlot}
              operatingHours={operatingHours}
              selectedDate={selectedDate}
              bookings={bookings}
            />

            {conflictError && (
              <Alert variant="destructive" className="mt-2">
                <AlertTriangle className="h-4 w-4 mr-2" />
                <AlertDescription>{conflictError}</AlertDescription>
              </Alert>
            )}
            
            {outsideOperatingHoursError && (
              <Alert variant="destructive" className="mt-2">
                <AlertTriangle className="h-4 w-4 mr-2" />
                <AlertDescription>{outsideOperatingHoursError}</AlertDescription>
              </Alert>
            )}
          </div>

          <div className={!isAuthenticated ? "opacity-90" : ""}>
            <CustomerForm
              note={note}
              onNoteChange={setNote}
              isAuthenticated={isAuthenticated}
              userProfile={userProfile}
            />
          </div>

          <BookingConfirmation
            isAuthenticated={isAuthenticated}
            isFormValid={isFormValid}
            isLoading={loading}
            onBookingSubmit={submitBooking}
            isProfileComplete={hasCompleteProfile}
            redirectToProfile={redirectToProfile}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
